import { useEffect, useRef } from 'react';
import { put } from './apiService';
import { getDevice, waitForDevice } from './deviceService';
import { logInfo, logError, logDebug } from './loggerService';
import type { Utm } from '../types/utm';

/** Map of URL query param names to Utm model fields */
const PARAM_MAP: Record<string, keyof Utm> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_id: 'utmId',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  variation: 'variation',
  promo_code: 'promoCode',
  email: 'email',
  l: 'lang',
};

let currentUtm: Utm | null = null;

/**
 * Read URL query params and build a Utm object if any tracked params are present.
 */
function captureUtmParams(): Partial<Omit<Utm, 'identifier'>> | null {
  const params = new URLSearchParams(window.location.search);
  const captured: Record<string, string> = {};

  for (const [paramName, modelField] of Object.entries(PARAM_MAP)) {
    const value = params.get(paramName);
    if (value) {
      captured[modelField] = value;
    }
  }

  // Language alone is not a marketing hit (a notification link may carry
  // `l`); it is recorded only alongside at least one marketing param.
  if (Object.keys(captured).filter((k) => k !== 'lang').length === 0) {
    return null;
  }

  return captured;
}

/**
 * Remove the tracked params from the address bar once captured, without a
 * reload. They are recorded; leaving them makes every reload or copied URL
 * report another landing. Other params (`ref`, `s`, …) are kept. The
 * language (`l`) was already applied by i18n at start-up and cached.
 *
 * `replaceState` rather than the router: InitUtm is mounted outside the
 * router, and nothing reads these params through it.
 */
function stripTrackedParams(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const name of Object.keys(PARAM_MAP)) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

/**
 * PUT the current UTM data to the backend.
 */
async function putUtm(): Promise<void> {
  if (!currentUtm) return;

  try {
    await put<void>('/marketing-data', currentUtm);
    logDebug('UTM data sent to backend');
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to PUT UTM data',
    );
  }
}

/**
 * Set the userId on the UTM record and PUT to backend.
 * Call after successful login. Only PUTs if UTM was captured on this session.
 */
export async function setUtmUserId(userId: string): Promise<void> {
  if (!currentUtm) return;

  currentUtm.userId = userId;
  logDebug('UTM userId set', undefined, { identifier: currentUtm.identifier, userId });
  await putUtm();
}

/**
 * Renderless component that captures UTM params from the URL on mount.
 * Only PUTs to backend if any tracked params are present.
 * Place in App.tsx after InitDevice (needs device identifier).
 *
 * Guarded by a ref so React's StrictMode double-mount doesn't double-fire
 * the PUT. This matters more than it used to: `marketing_data` is
 * deduplicated server-side so the second write collapsed harmlessly, but
 * `marketing_touch` is append-only and a double-fire reports 2x the real
 * click volume. Keyed by the captured params so a genuinely different
 * capture still processes.
 */
export function InitUtm() {
  const capturedRef = useRef<string | null>(null);

  useEffect(() => {
    const captured = captureUtmParams();
    if (!captured) return;

    const captureKey = JSON.stringify(captured);
    if (capturedRef.current === captureKey) return;
    capturedRef.current = captureKey;
    stripTrackedParams();

    waitForDevice().then(() => {
      const device = getDevice();
      if (!device) {
        logError(new Error('Device not initialized'), 'Cannot capture UTM after waiting for device');
        return;
      }

      currentUtm = {
        ...captured,
        identifier: device.identifier,
      } as Utm;

      logDebug('UTM params captured', undefined, currentUtm);
      putUtm();
    });
  }, []);

  return null;
}
